<template>
  <div v-if="enabledBranches.length > 1" class="relative flex items-center shrink-0">
    <div @click="isOpen = !isOpen" class="flex items-center space-x-3 cursor-pointer select-none">
      <img v-if="getFlagUrl(currentBranch) !== undefined"
           :src="getFlagUrl(currentBranch)"
           class="w-8 h-8 shrink-0 rounded-full object-cover border border-lightborder"
           alt="Flag"/>
      <div v-else class="rounded-full bg-primary w-8 h-8 flex items-center justify-center">
        <span class="text-white text-sm font-semibold">{{ getBranchCode(currentBranch) }}</span>
      </div>
      <p class="text-sm font-medium w-fit text-nowrap">{{ getBranchName(currentBranch) }}</p>
      <p class="text-sm font-semibold w-fit text-nowrap">{{ getBranchCode(currentBranch) }}</p>
      <img class="shrink-0 transition duration-150 ease-out" :class="{'rotate-180': isOpen === false}"
           src="@/assets/img/arrow.svg" alt="arrow">
    </div>

    <OutsideClickContainer class="top-12 right-0 !w-[250px]"
                           @outside-click="isOpen = false"
                           v-if="isOpen">
      <Transition name="slide-fade">
        <div v-if="isOpen"
             class="bg-white z-10 relative shadow-lg py-4 px-6 rounded-md flex-col flex justify-start space-y-3">
          <p class="text-xs text-light">Change branch</p>
          <div v-for="branch in enabledBranches" :key="branch.code"
               @click="chooseBranch(branch)"
               class="flex items-center justify-between cursor-pointer hover:text-primary">
            <div class="flex items-center space-x-3">
              <img v-if="getFlagUrl(branch) !== undefined"
                   :src="getFlagUrl(branch)"
                   class="w-6 h-6 rounded-full object-cover border border-lightborder"
                   alt="Flag"/>
              <div v-else class="rounded-full bg-primary w-6 h-6 flex items-center justify-center">
                <span class="text-white text-[10px] font-semibold">{{ branch.code }}</span>
              </div>
              <p :class="{'font-semibold text-primary': branch.id === branchesStore.currentBranchId}">{{ branch.name }}</p>
            </div>
            <div class="flex items-center space-x-2">
              <svg v-if="branch.id === branchesStore.currentBranchId"
                   class="w-4 h-4 text-primary" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"/>
              </svg>
              <p class="text-xs text-light">{{ branch.code }}</p>
            </div>
          </div>
        </div>
      </Transition>
    </OutsideClickContainer>
  </div>
</template>

<script setup lang="ts">
import {computed, ref} from "vue";
import Swal from "sweetalert2";
import type {SweetAlertResult} from "sweetalert2";
import {useAuthStore} from "@/stores/auth";
import {useBranchesStore} from "@/stores/branches";
import {Branch} from "@/types/site_admin/branches";
import {KPN} from "@/types/permissions/KPN";
import OutsideClickContainer from "@/components/OutsideClickContainer.vue";

const authStore = useAuthStore();
const branchesStore = useBranchesStore();
const isOpen = ref(false);

const flagUrls = import.meta.glob('@/assets/img/flags/1x1/*.svg', {eager: true, query: '?url', import: 'default'}) as Record<string, string>;

const enabledBranches = computed(() => {
  const enabled: Branch[] = [];
  branchesStore.branches.forEach(branch => {
    if (branch.is_enabled) {
      enabled.push(branch);
    }
  });

  return enabled;
});

const currentBranch = computed(() => {
  const branch = enabledBranches.value.find(branch => branch.id === branchesStore.currentBranchId);
  if (branch === undefined) {
    return null;
  }

  return branch;
});

const getFlagUrl = (branch: Branch | null) => {
  if (branch === null) {
    return undefined;
  }

  const fileName = '/' + branch.code.toLowerCase() + '.svg';
  const path = Object.keys(flagUrls).find(path => path.endsWith(fileName));
  if (path === undefined) {
    return undefined;
  }

  return flagUrls[path];
}

const getBranchName = (branch: Branch | null) => {
  if (branch === null) {
    return 'Select branch';
  }

  return branch.name;
}

const getBranchCode = (branch: Branch | null) => {
  if (branch === null) {
    return '';
  }

  return branch.code;
}

const chooseBranch = (branch: Branch) => {
  isOpen.value = false;
  if (branch.id === branchesStore.currentBranchId) {
    return;
  }

  Swal.fire({
    title: 'Change Branch',
    html: `Switch to <strong>${branch.name}</strong>? The page will reload.`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Switch',
  }).then((result: SweetAlertResult) => {
    if (result.isConfirmed) {
      branchesStore.setCurrentBranch(branch.id);
      window.location.reload();
    }
  });
}

const chooseDefaultBranch = () => {
  if (enabledBranches.value.length === 0) {
    return;
  }

  if (currentBranch.value !== null) {
    return;
  }

  let defaultBranch = enabledBranches.value.find(branch => branch.code === 'RO');
  if (defaultBranch === undefined) {
    defaultBranch = enabledBranches.value[0];
  }

  branchesStore.setCurrentBranch(defaultBranch.id);
}

if (authStore.accountPermissions.allowedAny([KPN.BRANCHES_ENABLED])) {
  branchesStore.getBranches()
      .then(() => {
        chooseDefaultBranch();
      });
}
</script>
